import React, { useEffect, useState } from "react";
import { Button, Input, Alert } from "@heroui/react";
import { useNavigate } from "react-router-dom";

const TwoFactorAuth = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState({
    enabled: false,
    configured: false,
    remainingBackupCodes: 0,
  });
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState("");
  const [secret, setSecret] = useState("");
  const [setupCode, setSetupCode] = useState("");
  const [regenerateCode, setRegenerateCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: "Bearer " + localStorage.getItem("jwt"),
  });

  const showSuccess = (message) => {
    setAlertMessage(message);
    setShowAlert(true);
    setShowErrorAlert(false);
    setTimeout(() => {
      setShowAlert(false);
      setAlertMessage("");
    }, 3000);
  };

  const showError = (message) => {
    setErrorMessage(message || "Something went wrong");
    setShowErrorAlert(true);
    setShowAlert(false);
  };

  const fetchStatus = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/admin/2fa/status`,
        { headers: authHeaders() }
      );
      const result = await response.json();
      if (!response.ok) {
        showError(result.message);
        return;
      }
      const enabled = Boolean(result.enabled);
      setStatus({
        enabled,
        configured: Boolean(result.configured),
        remainingBackupCodes: Number(result.remainingBackupCodes || 0),
      });
      localStorage.setItem("totpEnabled", enabled ? "1" : "0");
    } catch (err) {
      console.error(err);
      showError("Failed to load 2FA status");
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleSetup = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/admin/2fa/setup`,
        {
          method: "POST",
          headers: authHeaders(),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        showError(result.message);
        return;
      }
      setQrCodeDataUrl(result.qrCodeDataUrl);
      setSecret(result.secret);
      setSetupCode("");
      setBackupCodes([]);
      showSuccess("Scan the QR code, then enter a code to enable 2FA");
    } catch (err) {
      console.error(err);
      showError("Failed to start 2FA setup");
    } finally {
      setLoading(false);
    }
  };

  const handleEnable = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/admin/2fa/enable`,
        {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ token: setupCode.trim() }),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        showError(result.message);
        return;
      }
      setQrCodeDataUrl("");
      setSecret("");
      setSetupCode("");
      setBackupCodes(Array.isArray(result.backupCodes) ? result.backupCodes : []);
      localStorage.setItem("totpEnabled", "1");
      window.dispatchEvent(new Event("totpEnabledChanged"));
      await fetchStatus();
      showSuccess(
        result.message ||
          "Two-factor authentication enabled. Save your backup codes."
      );
    } catch (err) {
      console.error(err);
      showError("Failed to enable 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleRegenerateBackupCodes = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}/admin/2fa/backup-codes/regenerate`,
        {
          method: "POST",
          headers: authHeaders(),
          body: JSON.stringify({ token: regenerateCode.trim() }),
        }
      );
      const result = await response.json();
      if (!response.ok) {
        showError(result.message);
        return;
      }
      setRegenerateCode("");
      setBackupCodes(Array.isArray(result.backupCodes) ? result.backupCodes : []);
      await fetchStatus();
      showSuccess(result.message || "Backup codes regenerated");
    } catch (err) {
      console.error(err);
      showError("Failed to regenerate backup codes");
    } finally {
      setLoading(false);
    }
  };

  const copyBackupCodes = async () => {
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      showSuccess("Backup codes copied to clipboard");
    } catch (err) {
      console.error(err);
      showError("Could not copy backup codes");
    }
  };

  const normalizeTwoFactorInput = (value) =>
    value.replace(/[^a-zA-Z0-9-]/g, "").slice(0, 12).toUpperCase();

  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      {showAlert && (
        <Alert
          color="success"
          className="mb-4"
          onClose={() => setShowAlert(false)}
        >
          {alertMessage}
        </Alert>
      )}
      {showErrorAlert && (
        <Alert
          color="danger"
          className="mb-4"
          onClose={() => setShowErrorAlert(false)}
        >
          {errorMessage}
        </Alert>
      )}

      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Google Authenticator
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Two-factor authentication is mandatory for all admin accounts. You must
        set it up before using any other admin features. Use Google Authenticator
        or a compatible app; backup codes can be used if you lose access to your
        authenticator.
      </p>

      {!status.enabled && (
        <Alert color="warning" className="mb-4">
          Complete 2FA setup to unlock the admin panel. All other routes and
          actions are blocked until authenticator is enabled.
        </Alert>
      )}

      <div className="mb-6 flex flex-wrap gap-2">
        <span
          className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${
            status.enabled
              ? "bg-green-100 text-green-800"
              : "bg-amber-100 text-amber-800"
          }`}
        >
          Status: {status.enabled ? "Enabled" : "Setup required"}
        </span>
        {status.enabled && (
          <span className="inline-flex items-center px-3 py-1 rounded text-sm font-medium bg-blue-50 text-blue-800">
            Backup codes remaining: {status.remainingBackupCodes}
          </span>
        )}
      </div>

      {backupCodes.length > 0 && (
        <div className="mb-6 border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-3">
          <h4 className="text-sm font-semibold text-amber-900">
            Save your backup codes
          </h4>
          <p className="text-sm text-amber-800">
            These codes are shown only once. Each code can be used one time to
            sign in if you cannot use your authenticator app.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2 font-mono text-sm">
            {backupCodes.map((code) => (
              <li
                key={code}
                className="bg-white border border-amber-100 rounded px-3 py-2 select-all"
              >
                {code}
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap gap-2">
            <Button
              className="bg-amber-700 text-white"
              onClick={copyBackupCodes}
            >
              Copy codes
            </Button>
            <Button
              variant="bordered"
              onClick={() => {
                setBackupCodes([]);
                window.dispatchEvent(new Event("totpEnabledChanged"));
                navigate("/admin/dashboard");
              }}
            >
              I have saved these codes
            </Button>
          </div>
        </div>
      )}

      {!status.enabled && (
        <div className="space-y-4 border-t pt-4">
          <p className="text-sm text-gray-700">
            1. Install Google Authenticator on your phone.
            <br />
            2. Click <strong>Generate QR Code</strong> and scan it.
            <br />
            3. Enter the 6-digit code shown in the app to confirm.
            <br />
            4. Save the backup codes shown after enabling.
          </p>

          <Button
            className="bg-blue-600 text-white"
            onClick={handleSetup}
            isDisabled={loading}
          >
            {loading ? "Working..." : "Generate QR Code"}
          </Button>

          {qrCodeDataUrl && (
            <div className="mt-4 space-y-4">
              <div className="flex flex-col sm:flex-row gap-6 items-start">
                <img
                  src={qrCodeDataUrl}
                  alt="Google Authenticator QR code"
                  className="w-48 h-48 border border-gray-200 rounded"
                />
                <div className="text-sm text-gray-700">
                  <p className="mb-2">
                    If you cannot scan the QR code, enter this secret manually:
                  </p>
                  <code className="block bg-gray-100 px-3 py-2 rounded break-all select-all">
                    {secret}
                  </code>
                </div>
              </div>

              <form className="space-y-4 max-w-sm" onSubmit={handleEnable}>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Confirmation Code <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    placeholder="000000"
                    maxLength={6}
                    value={setupCode}
                    onChange={(e) =>
                      setSetupCode(
                        e.target.value.replace(/\D/g, "").slice(0, 6)
                      )
                    }
                    required
                    className="border border-gray-300 rounded-xl"
                  />
                </div>
                <Button
                  type="submit"
                  className="bg-blue-600 text-white"
                  isDisabled={loading || setupCode.length < 6}
                >
                  {loading ? "Enabling..." : "Enable Authenticator"}
                </Button>
              </form>
            </div>
          )}
        </div>
      )}

      {status.enabled && (
        <div className="space-y-8 border-t pt-4">
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-900">
              Regenerate backup codes
            </h4>
            <p className="text-sm text-gray-700">
              This invalidates all previous backup codes. Enter a current
              authenticator code or an unused backup code.
            </p>
            <form
              className="space-y-4 max-w-md"
              onSubmit={handleRegenerateBackupCodes}
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Authenticator or Backup Code{" "}
                  <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  placeholder="000000 or ABCD-EF12"
                  maxLength={12}
                  value={regenerateCode}
                  onChange={(e) =>
                    setRegenerateCode(normalizeTwoFactorInput(e.target.value))
                  }
                  required
                  className="border border-gray-300 rounded-xl"
                />
              </div>
              <Button
                type="submit"
                className="bg-blue-600 text-white"
                isDisabled={loading || regenerateCode.length < 6}
              >
                {loading ? "Regenerating..." : "Regenerate Backup Codes"}
              </Button>
            </form>
          </div>

          <p className="text-sm text-gray-600 border-t pt-4">
            Two-factor authentication is mandatory for admin accounts and cannot
            be disabled.
          </p>
        </div>
      )}
    </div>
  );
};

export default TwoFactorAuth;
